import cn from 'classnames'
import Image from 'next/image'
import {FC} from 'react'

import {getAboutUsContent} from '@/content/aboutUs'
import {TeamMemberType} from '@/content/team'
import {Link, useTranslation} from '@/i18n'
import {HOVER_TRANSITION} from '@/theme/cssTransitions'
import {theme} from '@/theme/theme'
import {routeTo} from '@/utils/router'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

import {Text, TextFontWeight, TextVariant} from '../content/Text'

type TeamMemberProps = {
  teamMember: TeamMemberType
  size?: 'small' | 'medium'
}

export const TeamMember: FC<TeamMemberProps> = ({teamMember, size = 'medium'}) => {
  const {t} = useTranslation()
  const {showMoreText} = getAboutUsContent()
  const {name, role, alternativeRole, photoSrc, photoAlt, slug} = teamMember

  const isMediumSize = size === 'medium'

  return (
    <>
      <div className="member">
        <div className={cn('photo', isMediumSize && 'medium')}>
          {slug ? (
            <Link href={routeTo.aboutUs.teamMember(slug)}>
              <Image
                src={photoSrc}
                alt={photoAlt}
                style={{objectFit: 'cover'}}
                fill
                sizes={`${MOBILE_SCREENSIZE} 180px, ${TABLET_SCREENSIZE} 230px, 256px`}
              />
              <div className="show-more">
                <Text
                  color={theme.palette.text.white}
                  variant={TextVariant.text14}
                  align="center"
                  fontWeight={TextFontWeight.bold}
                >
                  {t(showMoreText)}
                </Text>
              </div>
            </Link>
          ) : (
            <Image
              src={photoSrc}
              alt={photoAlt}
              style={{objectFit: 'cover'}}
              fill
              sizes={`${MOBILE_SCREENSIZE} 180px, ${TABLET_SCREENSIZE} 230px, 256px`}
            />
          )}
        </div>
        <div className="name">
          <Text
            variant={isMediumSize ? TextVariant.h6 : TextVariant.text16}
            element="p"
            fontWeight={TextFontWeight.bold}
            align="center"
          >
            {t(name)}
          </Text>
        </div>
        <div className="role">
          <Text
            color={theme.palette.text.light}
            variant={isMediumSize ? TextVariant.text16 : TextVariant.text14}
            align="center"
          >
            {t(alternativeRole ?? role)}
          </Text>
        </div>
      </div>

      <style jsx>{`
        .photo {
          position: relative;
          width: 100%;
          height: 200px;
          border-radius: 16px;
          overflow: hidden;
        }
        .medium {
          height: 256px;
        }
        .name {
          margin-top: ${theme.spacing[3]};
        }
        .role {
          margin-top: ${theme.spacing[1]};
        }
        .photo:has(:focus-visible) {
          outline: var(--focus-outline);
          outline-offset: 2px;
        }
        .photo:hover .show-more,
        .photo:has(:focus-visible) .show-more {
          opacity: 1;
          visibility: visible;
        }
        .show-more {
          opacity: 0;
          visibility: hidden;
          position: absolute;
          left: 12px;
          right: 12px;
          bottom: 12px;
          border-radius: 8px;
          padding: ${theme.spacing[2]};
          background-color: ${theme.palette.terracotta[400]};
          transition: opacity ${HOVER_TRANSITION}ms ease-in-out,
            visibility ${HOVER_TRANSITION}ms ease-in-out;
        }
        @media ${MOBILE_SCREENSIZE} {
          .photo {
            height: 180px;
          }
        }
      `}</style>
    </>
  )
}
